import moment from "moment-timezone";
import DeliveryModeEnum from "../../../../models/DeliveryModeEnum";
import IDeliveryDateRules from "../../../../models/IDeliveryDateRules";
import ISupplierModel from "../../../../models/ISupplierModel";
import IOrder from "../../../../models/IOrder";
import { DEFAULT_DELIVERY_TIMEZONE, isDeliveryDeadlinesEnabled, toDeliveryDateKey } from "./deliveryDeadlineUtils";

/**
 * Customer-selected delivery date (OR-1531): the client-side mirror of the API's
 * CustomerDeliveryDateService, so the checkout picker defaults and disables exactly what the server
 * accepts at submit.
 *
 * Earliest selectable date:
 *   - before the daily cutoff: today + lead days
 *   - at or after the cutoff:  today + lead days + 1
 *   - then roll forward past any blocked day of week
 * Example (1 PM cutoff, 2 lead days, none blocked): Mon 10 AM -> Wed; Mon 2 PM -> Thu; Fri 10 AM -> Sun.
 */

/** Notify code the API emits when a chosen date is no longer selectable (the earliest date rides along). */
export const DELIVERY_DATE_NOT_SELECTABLE_NOTIFY = "ERROR_DELIVERY_DATE_NOT_SELECTABLE";

/** `errorType` of the REST 422 body for the same rejection. */
export const DELIVERY_DATE_NOT_SELECTABLE_ERROR_TYPE = "DeliveryDateNotSelectable";

/** Upper bound on the lead time an account may configure (mirrors the API). */
export const MAX_DELIVERY_LEAD_DAYS = 60;

/** The account-level rules behind "Customer selects date" mode, as edited on the settings page. */
export interface ICustomerDeliveryDateSettings {
    /** "HH:mm" or "HH:mm:ss"; null / "" = no daily cutoff. */
    cutoffTime: string | null;
    leadDays: number;
    /** 0=Sun..6=Sat */
    blockedDays: number[];
}

export interface IDeliveryDateNotSelectableResponse {
    errorType: typeof DELIVERY_DATE_NOT_SELECTABLE_ERROR_TYPE;
    earliestDeliveryDate: string;
    requestedDate?: string | null;
    message?: string;
}

export const isDeliveryDateNotSelectableNotifyMessage = (message: unknown): boolean =>
    String(message || "").toUpperCase() === DELIVERY_DATE_NOT_SELECTABLE_NOTIFY;

export const isDeliveryDateNotSelectableResponse = (body: unknown): body is IDeliveryDateNotSelectableResponse =>
    !!body && typeof body === "object" && (body as { errorType?: unknown }).errorType === DELIVERY_DATE_NOT_SELECTABLE_ERROR_TYPE;

/** Whether a supplier (as loaded for staff) runs "Customer selects date" mode. */
export const isCustomerSelectsMode = (supplierModel?: Pick<ISupplierModel, "has_delivery_deadlines" | "delivery_mode"> | null): boolean =>
    !!supplierModel?.has_delivery_deadlines && supplierModel.delivery_mode === DeliveryModeEnum.CUSTOMER_SELECTS;

/**
 * Whether an order's delivery date should be shown on the customer-facing order screens (checkout
 * complete, order list, order detail, invoice). The date is meaningful whenever the account runs either
 * delivery mode; the vendor-schedule and rules-payload checks keep the slim order payloads (no Vendor,
 * no rules attached) and older supplier models working.
 */
export const showsDeliveryDate = (
    order?: Pick<IOrder, "delivery_date" | "deliveryDateRules"> & { Vendor?: IOrder["Vendor"] | null },
    supplierModel?: Pick<ISupplierModel, "has_delivery_deadlines"> | null
): boolean =>
    !!order?.delivery_date &&
    (!!supplierModel?.has_delivery_deadlines || !!order.deliveryDateRules || isDeliveryDeadlinesEnabled(order.Vendor));

/** Sorted, de-duplicated days of week out of whatever the API / form holds. */
export const normalizeBlockedDays = (value: unknown): number[] => {
    const list = Array.isArray(value) ? value : value === null || value === undefined || value === "" ? [] : [value];
    const days = new Set<number>();
    for (const entry of list) {
        const day = typeof entry === "number" ? entry : parseInt(String(entry), 10);
        if (Number.isInteger(day) && day >= 0 && day <= 6) days.add(day);
    }
    return Array.from(days).sort((a, b) => a - b);
};

const parseTime = (time?: string | null): { hour: number; minute: number } | null => {
    const match = /^([0-1]?\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/.exec(String(time || "").trim());
    return match ? { hour: parseInt(match[1], 10), minute: parseInt(match[2], 10) } : null;
};

/**
 * The earliest delivery date (YYYY-MM-DD) a customer may choose at `now` (defaults to the current time)
 * under `settings`, with the cutoff read in `timezone`.
 */
export const getEarliestSelectableDate = (
    settings: ICustomerDeliveryDateSettings,
    timezone?: string | null,
    now?: moment.Moment
): string => {
    const tz = timezone || DEFAULT_DELIVERY_TIMEZONE;
    const ref = (now ? now.clone() : moment()).tz(tz);
    const cutoff = parseTime(settings.cutoffTime);

    let base = ref.clone().startOf("day");
    if (cutoff) {
        const cutoffToday = ref.clone().hour(cutoff.hour).minute(cutoff.minute).second(0).millisecond(0);
        if (ref.isSameOrAfter(cutoffToday)) base = base.add(1, "day");
    }

    const leadDays = Number.isInteger(settings.leadDays) && settings.leadDays > 0 ? settings.leadDays : 0;
    const candidate = base.add(leadDays, "days");
    const blocked = new Set(normalizeBlockedDays(settings.blockedDays));

    // A fully blocked week is rejected at save time; the bound keeps a bad value from looping forever.
    for (let i = 0; i < 7 && blocked.has(candidate.day()); i++) candidate.add(1, "day");

    return candidate.format("YYYY-MM-DD");
};

/** The settings behind an API rules payload. */
export const settingsFromRules = (rules: IDeliveryDateRules): ICustomerDeliveryDateSettings => ({
    cutoffTime: rules.cutoffTime || null,
    leadDays: rules.leadDays,
    blockedDays: normalizeBlockedDays(rules.blockedDays),
});

/**
 * The earliest date the picker should offer right now: recomputed from the rules (the cutoff can pass
 * while the page sits open), never earlier than what the API resolved.
 */
export const getEarliestFromRules = (rules: IDeliveryDateRules, now?: moment.Moment): string => {
    const computed = getEarliestSelectableDate(settingsFromRules(rules), rules.timezone, now);
    return computed > rules.earliestDeliveryDate ? computed : rules.earliestDeliveryDate;
};

/**
 * Whether `date` (YYYY-MM-DD, or an Orders.delivery_date UTC-midnight timestamp) may be chosen under the
 * rules right now: a real date, on/after the earliest selectable date and not a blocked day of week.
 */
export const isSelectableDate = (date: string | Date | null | undefined, rules: IDeliveryDateRules, now?: moment.Moment): boolean => {
    const key = toDeliveryDateKey(date);
    if (!key) return false;
    const requested = moment.utc(key, "YYYY-MM-DD", true);
    if (!requested.isValid()) return false;
    if (new Set(normalizeBlockedDays(rules.blockedDays)).has(requested.day())) return false;
    return key >= getEarliestFromRules(rules, now);
};

/** A calendar day (local Date from the date picker) that may be chosen under the rules: same checks as isSelectableDate. */
export const isSelectableCalendarDay = (day: Date, rules: IDeliveryDateRules, now?: moment.Moment): boolean =>
    isSelectableDate(moment([day.getFullYear(), day.getMonth(), day.getDate()]).format("YYYY-MM-DD"), rules, now);

/** Form state for the settings page. */
export interface IDeliveryModeFormState {
    delivery_mode: DeliveryModeEnum;
    /** "HH:mm" for the time input. */
    delivery_cutoff_time: string;
    delivery_lead_days: string;
    delivery_blocked_days: number[];
}

export const supplierToDeliveryModeForm = (supplierModel: Partial<ISupplierModel>): IDeliveryModeFormState => ({
    delivery_mode:
        supplierModel.delivery_mode === DeliveryModeEnum.CUSTOMER_SELECTS
            ? DeliveryModeEnum.CUSTOMER_SELECTS
            : DeliveryModeEnum.FIXED_ROUTE,
    delivery_cutoff_time: supplierModel.delivery_cutoff_time ? String(supplierModel.delivery_cutoff_time).slice(0, 5) : "",
    delivery_lead_days:
        supplierModel.delivery_lead_days === null || supplierModel.delivery_lead_days === undefined
            ? "0"
            : String(supplierModel.delivery_lead_days),
    delivery_blocked_days: normalizeBlockedDays(supplierModel.delivery_blocked_days),
});

export type DeliveryModeFormErrors = Partial<Record<"delivery_cutoff_time" | "delivery_lead_days" | "delivery_blocked_days", string>>;

/** Validates the settings form; error values are i18n keys. */
export const validateDeliveryModeForm = (form: IDeliveryModeFormState): DeliveryModeFormErrors => {
    const errors: DeliveryModeFormErrors = {};
    if (form.delivery_mode !== DeliveryModeEnum.CUSTOMER_SELECTS) return errors;

    if (!parseTime(form.delivery_cutoff_time)) errors.delivery_cutoff_time = "delivery_cutoff_time_required";

    const leadDays = form.delivery_lead_days.trim() === "" ? 0 : Number(form.delivery_lead_days);
    if (!Number.isInteger(leadDays) || leadDays < 0 || leadDays > MAX_DELIVERY_LEAD_DAYS)
        errors.delivery_lead_days = "delivery_lead_days_invalid";

    if (normalizeBlockedDays(form.delivery_blocked_days).length >= 7) errors.delivery_blocked_days = "delivery_blocked_days_all";

    return errors;
};

/** The supplier fields to send through SupplierUpdateSupplier for the form. */
export const deliveryModeFormToSupplierChanges = (form: IDeliveryModeFormState): Partial<ISupplierModel> => ({
    delivery_mode: form.delivery_mode,
    delivery_cutoff_time: form.delivery_cutoff_time ? `${form.delivery_cutoff_time.slice(0, 5)}:00` : null,
    delivery_lead_days: form.delivery_lead_days.trim() === "" ? 0 : Number(form.delivery_lead_days),
    delivery_blocked_days: normalizeBlockedDays(form.delivery_blocked_days),
});
